import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useFilteredCities } from '../../../hooks/useFilteredCommunes';
import { useValidations } from '../../../hooks/useValidations';
import { formatOptions } from '../../../utils/selectFormat';
import { useAsyncOptionsFieldsLoader } from '../../../views/StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { SectionTitle } from '../../text';
import useClearInputs from '../hooks/useClearInputs';
import { DynamicContactType } from './RenderSections';

const MUSIC_SCHOOL_LOWER_CASE = 24;

type EducationalBackgroundReformadaSectionProps = {
  prefix: string;
  type: DynamicContactType;
  optionalFields?: string[];
  fieldNames?: {
    schoolId: string;
    highSchool: string;
    countryHighSchool: string;
    departmentHighSchool: string;
    cityHighSchool: string;
    graduationYear: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicEducationalBackgroundReformada = ({
  prefix,
  type,
  optionalFields = [],
  fieldNames = {
    schoolId: 'personalInformation_schoolId',
    highSchool: 'educationalInformation_graduatedSchool',
    countryHighSchool: 'educationalInformation_country',
    departmentHighSchool: 'educationalInformation_department',
    cityHighSchool: 'educationalInformation_city',
    graduationYear: 'educationalInformation_graduatedYear',
  },
  disabled = false,
}: EducationalBackgroundReformadaSectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();
  const { validateTextNotEmpty } = useValidations();
  const {
    data: { tenanCountry, countries, regions, cities } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const [selectedCountry, selectedDeparment, selectedSchool] = watch([
    fieldNames.countryHighSchool,
    fieldNames.departmentHighSchool,
    fieldNames.schoolId,
  ]);

  const memoCities = useMemo(() => {
    return (cities ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      regionId: c.regionId,
    }));
  }, [cities]);
  const filteredRegions = useFilteredCities(selectedDeparment, memoCities);

  const isRegionTouched =
    formState.touchedFields[fieldNames.departmentHighSchool];

  const optional = useCallback(
    (value: string, fieldName: string) => {
      if (optionalFields.includes(fieldName)) {
        return undefined;
      }
      return validateTextNotEmpty(value);
    },
    [optionalFields, validateTextNotEmpty],
  );

  const handleChangeOwnSustainer = useCallback(
    (next: boolean) => {
      if (!next) {
        setValue(fieldNames?.departmentHighSchool, null);
        setValue(fieldNames?.cityHighSchool, null);
        setValue(fieldNames?.graduationYear, '');
      }
    },
    [
      fieldNames?.departmentHighSchool,
      fieldNames?.cityHighSchool,
      fieldNames?.graduationYear,
      setValue,
    ],
  );

  useClearInputs({ type, handleChangeOwnSustainer: handleChangeOwnSustainer });

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.cityHighSchool, null);
    }
  }, [selectedDeparment, setValue, isRegionTouched, fieldNames.cityHighSchool]);

  // Memo
  const isColombia = useMemo(() => {
    return selectedCountry?.value === tenanCountry?.id;
  }, [selectedCountry?.value, tenanCountry]);

  const isMusicSchool = useMemo(() => {
    return Number(selectedSchool) === MUSIC_SCHOOL_LOWER_CASE;
  }, [selectedSchool]);

  const countryOptions = useMemo(() => {
    return formatOptions(countries ?? []);
  }, [countries]);

  const departmentOptions = useMemo(() => {
    return formatOptions(regions ?? []);
  }, [regions]);

  const fields = useMemo(() => {
    if (isMusicSchool) {
      return <></>;
    }

    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={isColombia ? 8 : 6}>
            <TextInput
              name={fieldNames.highSchool}
              label={t(`${prefix}.${fieldNames.highSchool}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.highSchool),
                },
              }}
              readOnly={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={isColombia ? 4 : 3}>
            <Select
              name={fieldNames.countryHighSchool}
              label={t(`${prefix}.${fieldNames.countryHighSchool}`)}
              options={countryOptions}
              control={control}
              rules={{
                validate: {
                  require: (value) =>
                    optional(value, fieldNames.countryHighSchool),
                },
              }}
              disabled={disabled}
            />
          </Col>
          {isColombia ? (
            <>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={fieldNames.departmentHighSchool}
                  label={t(`${prefix}.${fieldNames.departmentHighSchool}`)}
                  options={departmentOptions}
                  control={control}
                  rules={{
                    validate: {
                      require: (value) =>
                        optional(value, fieldNames.departmentHighSchool),
                    },
                  }}
                  disabled={disabled}
                />
              </Col>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={fieldNames.cityHighSchool}
                  label={t(`${prefix}.${fieldNames.cityHighSchool}`)}
                  options={filteredRegions}
                  control={control}
                  rules={{
                    validate: {
                      require: (value) =>
                        optional(value, fieldNames.cityHighSchool),
                    },
                  }}
                  shouldUnregister
                  disabled={disabled}
                />
              </Col>
            </>
          ) : (
            <></>
          )}
          <Col className="pb-3" xs={12} lg={isColombia ? 4 : 3}>
            <TextInput
              name={fieldNames.graduationYear}
              label={t(`${prefix}.${fieldNames.graduationYear}`)}
              control={control}
              readOnly={disabled}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    fieldNames.countryHighSchool,
    fieldNames.cityHighSchool,
    fieldNames.departmentHighSchool,
    fieldNames.graduationYear,
    fieldNames.highSchool,
    disabled,
    t,
    optional,
    isColombia,
    isMusicSchool,
    departmentOptions,
    countryOptions,
    filteredRegions,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicEducationalBackgroundReformada;
